import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ExpandableCaptionProps {
  /** Caption content; may include inline markup such as the bold handle. */
  children: ReactNode;
  /** Classes for the caption text element. */
  className?: string;
  /** Classes for the more/less toggle. */
  toggleClassName?: string;
  /**
   * Extra classes applied to the caption text only while expanded. Frames that
   * can't scroll (TikTok) pass a bounded max-height + scroll here so a long
   * expanded caption scrolls in place instead of overflowing the fixed frame.
   */
  expandedClassName?: string;
  /** Forwarded to the caption element so tests/consumers can query it. */
  'data-testid'?: string;
}

/**
 * Caption that clamps to two lines and reveals the rest behind a "more" /
 * "less" toggle (Instagram-style), instead of scrolling within a fixed height.
 * The toggle only appears when the collapsed caption is actually truncated.
 *
 * `wrap-anywhere` is load-bearing. One unbroken token (a URL, a run-on hashtag)
 * overflows the frame horizontally, which the clamp cannot see, so the caption
 * clips with no toggle. It must be `anywhere` and not `break-word`: X nests the
 * caption in a `flex-1` column sized to its min-content width, which only
 * `anywhere` shrinks.
 *
 * Overflow is measured via a callback ref attached to the paragraph, so React
 * runs it exactly when the DOM node attaches. A `ResizeObserver` (returned as
 * callback-ref cleanup, React 19 pattern) keeps `isTruncated` in sync when the
 * frame reflows — no extra effect needed. It never measures while expanded:
 * only the clamped box can report overflow.
 *
 * A single `useLayoutEffect` remains to handle a caption text swap on a
 * still-mounted instance (variant regeneration, live edit): it collapses
 * expanded state and re-measures truncation against the clamped layout. It's a
 * text-swap-detect, not a measurement chain — the second chained measurement
 * effect the original file had is gone, absorbed by the ResizeObserver.
 */
export const ExpandableCaption = ({
  children,
  className = '',
  toggleClassName = 'mt-0.5 block text-[13px] font-semibold text-black/55',
  expandedClassName = '',
  'data-testid': dataTestId,
}: ExpandableCaptionProps) => {
  const { t } = useTranslation('contentTest');
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  // Tracks the last measured caption text so a text swap on a still-mounted
  // instance can collapse expanded state without a chain of effects.
  const captionTextRef = useRef<string | null>(null);
  const textNodeRef = useRef<HTMLParagraphElement | null>(null);

  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  // Overflow is only observable on the clamped box: an expanded paragraph
  // reports `scrollHeight === clientHeight`.
  const measureClamped = useCallback((node: HTMLParagraphElement) => {
    setIsTruncated(node.scrollHeight > node.clientHeight + 1);
  }, []);

  // Guarded entry point for the attach/observer paths. Expanding swaps
  // `line-clamp-2` for `expandedClassName` (empty for every preview except
  // TikTok), so the node resizes and the observer fires; measuring then would
  // clear `isTruncated`, unmount the "less" toggle and leave the caption
  // permanently expanded. Never re-measure while expanded.
  const measure = useCallback(
    (node: HTMLParagraphElement) => {
      if (expandedRef.current) return;
      measureClamped(node);
    },
    [measureClamped],
  );

  const attachCaption = useCallback(
    (node: HTMLParagraphElement | null) => {
      textNodeRef.current = node;
      if (!node) return;
      captionTextRef.current = node.textContent ?? '';
      measure(node);
      const observer = new ResizeObserver(() => measure(node));
      observer.observe(node);
      return () => observer.disconnect();
    },
    [measure],
  );

  // After each commit, detect a caption text change, collapse if needed, and
  // re-measure truncation for the NEW text. `children` is a fresh JSX reference
  // every render, so we key on textContent (a stable string) rather than the
  // ReactNode identity; the text-identity ref guards against redundant setState.
  //
  // A swap that happens while expanded needs two passes, because the node still
  // carries the expanded class in the commit where the new text arrives and an
  // unclamped paragraph reports `scrollHeight === clientHeight`:
  //   pass 1 — collapse, and deliberately do NOT record the new text;
  //   pass 2 — `expanded` is a dependency, so the collapse commit re-runs this
  //            effect with `line-clamp-2` applied; the text still mismatches, so
  //            it records and measures against the clamped box.
  // Handing pass 2 to the ResizeObserver instead would be a silent bug: the
  // browser only delivers a resize when the box size actually changes, so a new
  // caption whose clamped height equals the old expanded height would keep a
  // stale `isTruncated === true` and render a "more" toggle that expands nothing.
  useLayoutEffect(() => {
    const el = textNodeRef.current;
    if (!el) return;
    const text = el.textContent ?? '';
    if (text === captionTextRef.current) return;

    if (expanded) {
      expandedRef.current = false;
      setExpanded(false);
      return;
    }

    captionTextRef.current = text;
    measureClamped(el);
  }, [children, expanded, measureClamped]);

  return (
    <>
      <p
        ref={attachCaption}
        data-testid={dataTestId}
        className={`wrap-anywhere ${className} ${expanded ? expandedClassName : 'line-clamp-2'}`.trim()}
      >
        {children}
      </p>
      {isTruncated && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          className={toggleClassName}
        >
          {expanded
            ? t('contentTest.expandableCaption.less')
            : t('contentTest.expandableCaption.more')}
        </button>
      )}
    </>
  );
};

ExpandableCaption.displayName = 'ExpandableCaption';
